import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { useEffect } from 'react'
import Portal from '~components/root/portal'
import Icon from '~components/ui/icon'
import { cn } from '~utils/cn'

type ToastMessage =
  | 'Something went wrong. Please try again later.'
  | (string & NonNullable<unknown>)

type ToastType = 'default' | 'success' | 'caution' | 'error'

type IconColorCss = '' | 'fill-green-500' | 'fill-orange-500' | 'fill-red-500'
type IconColorTypeCss = { [key in ToastType]: IconColorCss }

export type ToastProps = Readonly<{
  message: ToastMessage
  type?: ToastType
  iconColor?: IconColorCss
  /**
   * toast.open() 사용 시 true,
   * toast.close() 사용 시 false가 자동으로 할당됩니다.
   */
  isOpen?: boolean
  autoClosingTime?: number
  position?: 'top' | 'bottom'
}>

const toastAtom = atom<ToastProps | null>(null)
toastAtom.debugLabel = 'toastAtom'

const store = getDefaultStore()

const DEFAULT_CLOSING_TIME = 5000

export const toast = {
  open(props: ToastProps) {
    store.set(toastAtom, {
      ...props,
      type: props.type ?? 'default',
      isOpen: true,
      autoClosingTime: props.autoClosingTime ?? DEFAULT_CLOSING_TIME,
    })
  },
  close() {
    const current = store.get(toastAtom)

    store.set(toastAtom, {
      ...current,
      message: current?.message || '',
      isOpen: false,
    })
  },
  destroy() {
    store.set(toastAtom, null)
  },
}

const ICON_COLOR_TYPE_CSS: IconColorTypeCss = {
  default: '',
  success: 'fill-green-500',
  caution: 'fill-orange-500',
  error: 'fill-red-500',
}

const ToastIcon = ({
  type = 'default',
  iconColor = '',
}: Pick<ToastProps, 'type' | 'iconColor'>) => {
  const _iconColor = iconColor || ICON_COLOR_TYPE_CSS[type]
  const iconStyle = cn(_iconColor, 'h-5 w-5')

  switch (type) {
    case 'success':
      return <Icon name="check_circle_solid" className={iconStyle} />
    case 'caution':
      return <Icon name="circle_exclamation" className={iconStyle} />
    case 'error':
      return <Icon name="remove_circle_solid" className={iconStyle} />
    default:
      return null
  }
}

/**
 * @version v1.0.0
 * @author Danny
 */
const ToastComponent = () => {
  const props = useAtomValue(toastAtom)

  useEffect(() => {
    if (!props) {
      return
    }

    const { isOpen = false, autoClosingTime = DEFAULT_CLOSING_TIME } = props

    if (isOpen && autoClosingTime) {
      const close = setTimeout(toast.close, autoClosingTime)
      const destroy = setTimeout(toast.destroy, autoClosingTime + 1000)

      return () => {
        clearTimeout(close)
        clearTimeout(destroy)
      }
    }
  }, [props])

  return (
    <Portal id="toast">
      <div className="relative flex w-full justify-center">
        <div
          className={cn(
            'fixed  z-toast w-fit px-6',
            props?.isOpen ? 'visible' : 'invisible',
            props?.position === 'bottom' ? 'bottom-10' : 'top-10'
          )}
        >
          <div
            role="none"
            className={cn(
              'h-fit w-fit cursor-pointer items-center justify-between rounded-2xl bg-gray-900/80 px-4 py-3 transition-all duration-500',
              props?.isOpen ? 'translate-y-0 ease-out' : '-translate-y-[300%] opacity-0 ease-in'
            )}
            onClick={toast.close}
            onKeyDown={toast.close}
            onTouchStart={toast.close}
          >
            <div className="flex w-fit items-start">
              {props?.type !== 'default' && (
                <div className="mr-2 [&>svg]:h-5 [&>svg]:w-5">
                  <ToastIcon type={props?.type} iconColor={props?.iconColor} />
                </div>
              )}
              <div className="flex w-fit flex-col">
                <span className="w-fit whitespace-pre-line text-white text-title-3">
                  {props?.message}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default ToastComponent
